import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inputClass } from '../components/ui/inputClass';
import { BrandMark } from '../components/ui/BrandMark';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const { register } = useAuth();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const res = await register(email, password, displayName);
      setRegisteredEmail(res.email);
    } catch { setErr('Registration failed. Please try again.'); }
    finally { setBusy(false); }
  }

  if (registeredEmail) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <BrandMark />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Check your inbox</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
              We sent a confirmation link to <strong>{registeredEmail}</strong>.
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-3 shadow-sm text-sm text-slate-600 dark:text-slate-300">
            <p>Click the link in that email to activate your account, then sign in.</p>
            <p>
              Didn't get it?{' '}
              <Link className="font-medium text-teal-600 dark:text-teal-400 hover:underline" to="/resend-confirmation">
                Resend confirmation email
              </Link>
            </p>
            <p>
              <Link className="font-medium text-teal-600 dark:text-teal-400 hover:underline" to="/login">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <BrandMark />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Create account</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Start tracking your catches</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-4 shadow-sm">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
            <input
              className={inputClass}
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Display name</label>
            <input
              className={inputClass}
              type="text"
              required
              placeholder="Your name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
            <input
              className={inputClass}
              type="password"
              required
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          {err && <p className="text-sm text-red-500">{err}</p>}
          <button
            className="w-full rounded-lg bg-teal-600 dark:bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            disabled={busy}
          >
            {busy ? 'Creating account…' : 'Create account'}
          </button>
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Have an account?{' '}
            <Link className="font-medium text-teal-600 dark:text-teal-400 hover:underline" to="/login">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
